import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import { http, unwrapApiResponse } from '../api/http';
import { API_BASE } from '../config';
import { PageHeader } from '../components/PageHeader';
import { ErrorBanner } from '../components/ErrorBanner';
import { useAuth } from '../auth/AuthContext';
import { useAppUi } from '../app/useAppUi';

const SECTIONS = ['author', 'group', 'umra'];
const PRICE_KEYS = ['quad', 'trpl', 'dbl', 'sgl', 'pp'];

/** Posters are served at <origin>/media/... (outside /api/v1). */
const mediaUrl = (path) => (path ? `${API_BASE.replace(/\/api\/v1\/?$/, '')}${path}` : null);

const todayIso = () => new Date().toISOString().slice(0, 10);

const addDays = (iso, days) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

const emptyDeparture = () => ({ date_from: '', date_to: '', prices: {} });

const emptyForm = (section = 'author') => ({
  section,
  operator_name: '',
  title: '',
  country: '',
  cities: '',
  days: '',
  nights: '',
  currency: 'USD',
  sort_order: 0,
  is_active: true,
  flight_info: '',
  baggage: '',
  hotels: [],
  departures: [emptyDeparture()],
  program: [],
  included: '',
  excluded: '',
  notes: '',
  contacts: '',
});

const rowToForm = (row) => ({
  ...emptyForm(row.section),
  ...Object.fromEntries(
    ['section', 'operator_name', 'title', 'country', 'cities', 'currency', 'flight_info', 'baggage', 'notes', 'contacts']
      .map((k) => [k, row[k] ?? '']),
  ),
  days: row.days ?? '',
  nights: row.nights ?? '',
  sort_order: row.sort_order ?? 0,
  is_active: row.is_active !== false,
  hotels: (row.hotels || []).map((h) => ({ city: h.city || '', nights: h.nights ?? '', name: h.name || '', meal: h.meal || '' })),
  departures: (row.departures || []).map((d) => ({
    date_from: d.date_from || '',
    date_to: d.date_to || '',
    prices: Object.fromEntries(Object.entries(d.prices || {}).map(([k, v]) => [k, String(v)])),
  })),
  program: (row.program || []).map((p) => ({ day: p.day ?? '', date: p.date || '', text: p.text || '' })),
  included: (row.included || []).join('\n'),
  excluded: (row.excluded || []).join('\n'),
});

const toPayload = (form) => ({
  ...form,
  days: form.days === '' ? null : Number(form.days),
  nights: form.nights === '' ? null : Number(form.nights),
  sort_order: Number(form.sort_order) || 0,
  hotels: form.hotels.filter((h) => h.name.trim()),
  departures: form.departures
    .filter((d) => d.date_from)
    .map((d) => ({
      date_from: d.date_from,
      date_to: d.date_to || null,
      prices: Object.fromEntries(
        PRICE_KEYS.filter((k) => Number(d.prices[k]) > 0).map((k) => [k, Number(d.prices[k])]),
      ),
    })),
  program: form.program.filter((p) => p.text.trim()),
});

const formIsValid = (form) =>
  Boolean(form.operator_name.trim() && form.title.trim()) &&
  form.departures.some((d) => d.date_from && PRICE_KEYS.some((k) => Number(d.prices[k]) > 0));

/** Small row-list editor: one line of inputs per item, with add / remove. */
function RowsEditor({ title, rows, onChange, makeRow, renderRow, addLabel, extraActions }) {
  const update = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">{title}</Typography>
        <Stack direction="row" spacing={1}>
          {extraActions}
          <Button size="small" startIcon={<AddIcon />} onClick={() => onChange([...rows, makeRow(rows)])}>
            {addLabel}
          </Button>
        </Stack>
      </Stack>
      <Stack spacing={1}>
        {rows.map((row, i) => (
          <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            {renderRow(row, (patch) => update(i, patch))}
            <IconButton size="small" onClick={() => onChange(rows.filter((_, j) => j !== i))} aria-label="remove">
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

function TourEditor({ open, initial, tourId, posterUrl, source = 'manual', onClose, onSaved, t }) {
  const synced = source !== 'manual';
  const [form, setForm] = React.useState(initial);
  const [posterFile, setPosterFile] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (open) {
      setForm(initial);
      setPosterFile(null);
      setError(null);
    }
  }, [open, initial]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const field = (key, props = {}) => (
    <TextField
      label={t(`curatedTours.f.${key}`)}
      value={form[key]}
      onChange={(e) => set({ [key]: e.target.value })}
      size="small"
      fullWidth
      {...props}
    />
  );

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = toPayload(form);
      const res = tourId
        ? await http.put(`/admin/curated-tours/${tourId}`, payload)
        : await http.post('/admin/curated-tours', payload);
      const saved = unwrapApiResponse(res);
      if (posterFile && saved?.id) {
        const fd = new FormData();
        fd.append('file', posterFile);
        await http.put(`/admin/curated-tours/${saved.id}/poster`, fd);
      }
      onSaved();
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  };

  const removePoster = async () => {
    if (!tourId) return;
    setSaving(true);
    try {
      await http.delete(`/admin/curated-tours/${tourId}/poster`);
      onSaved({ keepOpen: true });
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  };

  // Weekly packages (Umrah): copy the last departure one week later, same prices.
  const addNextWeek = () => {
    const last = form.departures[form.departures.length - 1];
    if (!last?.date_from) return;
    set({
      departures: [
        ...form.departures,
        { date_from: addDays(last.date_from, 7), date_to: last.date_to ? addDays(last.date_to, 7) : '', prices: { ...last.prices } },
      ],
    });
  };

  const previewUrl = posterFile ? URL.createObjectURL(posterFile) : mediaUrl(posterUrl);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle>{tourId ? t('curatedTours.editTitle', { id: tourId }) : t('curatedTours.createTitle')}</DialogTitle>
      <DialogContent dividers>
        <ErrorBanner error={error} />
        {synced ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('curatedTours.syncedNotice', { operator: initial.operator_name })}
          </Alert>
        ) : null}
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              select
              size="small"
              label={t('curatedTours.f.section')}
              value={form.section}
              onChange={(e) => set({ section: e.target.value })}
              sx={{ minWidth: 180 }}
            >
              {SECTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {t(`curatedTours.section.${s}`)}
                </MenuItem>
              ))}
            </TextField>
            {field('operator_name', { required: true })}
            <FormControlLabel
              control={<Switch checked={form.is_active} onChange={(e) => set({ is_active: e.target.checked })} />}
              label={t('curatedTours.f.is_active')}
              sx={{ flexShrink: 0 }}
            />
          </Stack>
          {field('title', { required: true })}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            {field('country')}
            {field('cities')}
          </Stack>
          <Stack direction="row" spacing={1.5}>
            {field('days', { type: 'number' })}
            {field('nights', { type: 'number' })}
            {field('currency')}
            {field('sort_order', { type: 'number', helperText: t('curatedTours.sortHint') })}
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
            <Box sx={{ width: 140, flexShrink: 0 }}>
              {previewUrl ? (
                <Box component="img" src={previewUrl} alt="" sx={{ width: '100%', borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
              ) : (
                <Box sx={{ height: 100, borderRadius: 1, border: '1px dashed', borderColor: 'divider', display: 'grid', placeItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">{t('curatedTours.noPoster')}</Typography>
                </Box>
              )}
            </Box>
            <Stack spacing={1}>
              <Button variant="outlined" size="small" component="label" disabled={synced}>
                {t('curatedTours.choosePoster')}
                <input
                  hidden
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => setPosterFile(e.target.files?.[0] || null)}
                />
              </Button>
              {tourId && posterUrl && !posterFile ? (
                <Button size="small" color="error" onClick={removePoster} disabled={saving}>
                  {t('curatedTours.removePoster')}
                </Button>
              ) : null}
              <Typography variant="caption" color="text.secondary">{t('curatedTours.posterHint')}</Typography>
            </Stack>
          </Stack>

          <Divider />
          {field('flight_info', { multiline: true, minRows: 2 })}
          {field('baggage')}

          <RowsEditor
            title={t('curatedTours.f.hotels')}
            addLabel={t('curatedTours.add')}
            rows={form.hotels}
            onChange={(hotels) => set({ hotels })}
            makeRow={() => ({ city: '', nights: '', name: '', meal: '' })}
            renderRow={(h, update) => (
              <>
                <TextField size="small" label={t('curatedTours.f.city')} value={h.city} onChange={(e) => update({ city: e.target.value })} sx={{ minWidth: 120, flexShrink: 0 }} />
                <TextField size="small" type="number" label={t('curatedTours.f.nights')} value={h.nights} onChange={(e) => update({ nights: e.target.value })} sx={{ width: 100, flexShrink: 0 }} />
                <TextField size="small" label={t('curatedTours.f.hotel')} value={h.name} onChange={(e) => update({ name: e.target.value })} fullWidth />
                <TextField size="small" label={t('curatedTours.f.meal')} value={h.meal} onChange={(e) => update({ meal: e.target.value })} fullWidth />
              </>
            )}
          />

          <RowsEditor
            title={t('curatedTours.f.departures')}
            addLabel={t('curatedTours.add')}
            rows={form.departures}
            onChange={(departures) => set({ departures })}
            makeRow={emptyDeparture}
            extraActions={
              <Tooltip title={t('curatedTours.nextWeekHint')}>
                <span>
                  <Button size="small" startIcon={<EventRepeatIcon />} onClick={addNextWeek} disabled={!form.departures.at(-1)?.date_from}>
                    {t('curatedTours.nextWeek')}
                  </Button>
                </span>
              </Tooltip>
            }
            renderRow={(d, update) => (
              <>
                <TextField size="small" type="date" label={t('curatedTours.f.date_from')} value={d.date_from} onChange={(e) => update({ date_from: e.target.value })} InputLabelProps={{ shrink: true }} sx={{ minWidth: 150, flexShrink: 0 }} />
                <TextField size="small" type="date" label={t('curatedTours.f.date_to')} value={d.date_to} onChange={(e) => update({ date_to: e.target.value })} InputLabelProps={{ shrink: true }} sx={{ minWidth: 150, flexShrink: 0 }} />
                {PRICE_KEYS.map((k) => (
                  <TextField
                    key={k}
                    size="small"
                    type="number"
                    label={t(`curatedTours.price.${k}`)}
                    value={d.prices[k] ?? ''}
                    onChange={(e) => update({ prices: { ...d.prices, [k]: e.target.value } })}
                    sx={{ width: 96, flexShrink: 0 }}
                  />
                ))}
              </>
            )}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
            {t('curatedTours.pricesHint')}
          </Typography>

          <RowsEditor
            title={t('curatedTours.f.program')}
            addLabel={t('curatedTours.add')}
            rows={form.program}
            onChange={(program) => set({ program })}
            makeRow={(rows) => ({ day: rows.length + 1, date: '', text: '' })}
            renderRow={(p, update) => (
              <>
                <TextField size="small" type="number" label={t('curatedTours.f.day')} value={p.day} onChange={(e) => update({ day: e.target.value })} sx={{ width: 80, flexShrink: 0 }} />
                <TextField size="small" label={t('curatedTours.f.date')} value={p.date} onChange={(e) => update({ date: e.target.value })} sx={{ width: 120, flexShrink: 0 }} />
                <TextField size="small" multiline label={t('curatedTours.f.text')} value={p.text} onChange={(e) => update({ text: e.target.value })} fullWidth />
              </>
            )}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            {field('included', { multiline: true, minRows: 4, helperText: t('curatedTours.onePerLine') })}
            {field('excluded', { multiline: true, minRows: 4, helperText: t('curatedTours.onePerLine') })}
          </Stack>
          {field('notes', { multiline: true, minRows: 2 })}
          {field('contacts', { multiline: true, minRows: 2 })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('common.cancel')}
        </Button>
        <Button variant="contained" onClick={save} disabled={synced || saving || !formIsValid(form)}>
          {saving ? t('common.saving') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function CuratedToursPage() {
  const { t } = useAppUi();
  const { admin } = useAuth();
  const canWrite = admin?.role === 'SUPER_ADMIN' || admin?.role === 'ADMIN';

  const [section, setSection] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [rows, setRows] = React.useState([]);
  const [editor, setEditor] = React.useState({ open: false, row: null, initial: emptyForm() });
  const [deleting, setDeleting] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await http.get('/admin/curated-tours', { params: section ? { section } : {} });
      setRows(unwrapApiResponse(res) || []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [section]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreate = () => setEditor({ open: true, row: null, initial: emptyForm(section || 'author') });
  const openEdit = (row) => setEditor({ open: true, row, initial: rowToForm(row) });

  const onSaved = async ({ keepOpen = false } = {}) => {
    const res = await http.get('/admin/curated-tours', { params: section ? { section } : {} });
    const list = unwrapApiResponse(res) || [];
    setRows(list);
    if (keepOpen && editor.row) {
      setEditor((ed) => ({ ...ed, row: list.find((r) => r.id === ed.row.id) || ed.row }));
    } else {
      setEditor((ed) => ({ ...ed, open: false }));
    }
  };

  const toggleActive = async (row) => {
    setError(null);
    try {
      await http.put(`/admin/curated-tours/${row.id}`, { is_active: !row.is_active });
      await fetchData();
    } catch (e) {
      setError(e);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setSaving(true);
    try {
      await http.delete(`/admin/curated-tours/${deleting.id}`);
      setDeleting(null);
      await fetchData();
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  };

  const today = todayIso();
  const columns = React.useMemo(
    () => [
      { field: 'id', headerName: 'ID', width: 70 },
      {
        field: 'poster_url',
        headerName: t('curatedTours.colPoster'),
        width: 80,
        sortable: false,
        renderCell: (p) =>
          p.value ? <Box component="img" src={mediaUrl(p.value)} alt="" sx={{ height: 40, width: 56, objectFit: 'cover', borderRadius: 0.5, mt: 0.5 }} /> : '—',
      },
      { field: 'section', headerName: t('curatedTours.colSection'), width: 110, valueGetter: (v) => t(`curatedTours.section.${v}`) },
      {
        field: 'operator_name',
        headerName: t('curatedTours.f.operator_name'),
        width: 190,
        renderCell: (p) => (
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ height: '100%' }}>
            <span>{p.value}</span>
            {p.row.source && p.row.source !== 'manual' ? <Chip size="small" color="primary" variant="outlined" label={t('curatedTours.auto')} /> : null}
          </Stack>
        ),
      },
      { field: 'title', headerName: t('curatedTours.f.title'), flex: 1, minWidth: 260 },
      {
        field: 'departures',
        headerName: t('curatedTours.colUpcoming'),
        width: 170,
        sortable: false,
        valueGetter: (v) => {
          const upcoming = (v || []).filter((d) => d.date_from >= today);
          return upcoming.length ? `${upcoming[0].date_from} (${upcoming.length})` : t('curatedTours.noUpcoming');
        },
      },
      {
        field: 'is_active',
        headerName: t('curatedTours.f.is_active'),
        width: 100,
        renderCell: (p) => <Switch size="small" checked={p.value} disabled={!canWrite} onChange={() => toggleActive(p.row)} />,
      },
      {
        field: 'actions',
        headerName: '',
        width: 100,
        sortable: false,
        renderCell: (p) => (
          <Stack direction="row">
            <IconButton size="small" disabled={!canWrite} onClick={() => openEdit(p.row)} aria-label={t('common.edit')}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" disabled={!canWrite || (p.row.source && p.row.source !== 'manual')} onClick={() => setDeleting(p.row)} aria-label={t('common.delete')}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [t, canWrite, today],
  );

  return (
    <Box>
      <PageHeader
        title={t('curatedTours.title')}
        subtitle={t('curatedTours.subtitle')}
        right={
          <Stack direction="row" spacing={1}>
            {canWrite ? (
              <Button variant="contained" onClick={openCreate}>
                {t('curatedTours.create')}
              </Button>
            ) : null}
            <Button variant="outlined" onClick={fetchData} disabled={loading}>
              {t('common.refresh')}
            </Button>
          </Stack>
        }
      />

      <ErrorBanner error={error} />

      <Tabs value={section} onChange={(_, v) => setSection(v)} sx={{ mb: 1.5 }}>
        <Tab value="" label={t('common.all')} />
        {SECTIONS.map((s) => (
          <Tab key={s} value={s} label={t(`curatedTours.section.${s}`)} />
        ))}
      </Tabs>

      <Box sx={{ height: 640, bgcolor: 'background.paper', borderRadius: 2, boxShadow: (th) => th.custom.shadow, overflow: 'hidden' }}>
        <DataGrid rows={rows} columns={columns} loading={loading} density="compact" disableRowSelectionOnClick />
      </Box>

      <TourEditor
        open={editor.open}
        initial={editor.initial}
        tourId={editor.row?.id}
        posterUrl={editor.row?.poster_url}
        source={editor.row?.source}
        onClose={() => setEditor((ed) => ({ ...ed, open: false }))}
        onSaved={onSaved}
        t={t}
      />

      <Dialog open={Boolean(deleting)} onClose={() => setDeleting(null)} fullWidth maxWidth="xs">
        <DialogTitle>{t('curatedTours.deleteTitle')}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            {t('curatedTours.deleteConfirm', { title: deleting?.title ?? '' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" color="error" onClick={confirmDelete} disabled={saving}>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
